import MDEditor from '@uiw/react-md-editor';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import { NoteOwnerType } from '@/types';
import { Tab, Tabs, Typography } from '@/ui';

import Notes from '@/components/Notes/Notes';
import Banner from '@/components/Profile/Banner';
import Bio from '@/components/Profile/Bio';
import Details from '@/components/Profile/Coach/Details';
import EditCoachAdmin from '@/components/Profile/Coach/Edit/Admin';
import EditCoach from '@/components/Profile/Coach/Edit/Coach';
import Experience from '@/components/Profile/Coach/Experience';
import EditCoachFunctions from '@/components/Profile/Coach/Functions/Edit';
import Styles from '@/components/Profile/Coach/Styles';
import EditCoachSubFunctions from '@/components/Profile/Coach/SubFunctions/Edit';
import CoachSummary from '@/components/Profile/Coach/Summary';
import { coachStatusesLabel } from '@/constants/coachStatus';
import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import { CoachSchedulePage } from '@/features/coachSchedule';
import { useGetCoachStatusQuery, useGetUserQuery } from '@/store/api/userApi';
import { hasGlobalRole, isAdmin, isCoach } from '@/types/user';

interface CoachAdminProfileProps {
  initialTabIndex?: number;
}

// TODO Combine with src/pages/profile
const Profile = ({ initialTabIndex = 0 }: CoachAdminProfileProps) => {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { user: currentUser, isUserLoading } = useCurrentUser();

  const [isEditing, setIsEditing] = useState(false);
  const [isEditingAdminDetails, setIsEditingAdminDetails] = useState(false);

  const {
    data: coach,
    isLoading,
    refetch,
  } = useGetUserQuery(id, {
    skip: !id,
  });

  const showAdminOnly = isAdmin(currentUser);
  const canManageCoachStatus = hasGlobalRole(currentUser, 'admin');
  const canViewCoachStatus =
    canManageCoachStatus || hasGlobalRole(currentUser, 'support');
  const {
    currentData: coachStatusData,
    isError: coachStatusError,
    isFetching: coachStatusFetching,
    isLoading: coachStatusLoading,
  } = useGetCoachStatusQuery(id, {
    skip: !id || !canViewCoachStatus,
  });
  let coachStatusText = coachStatusesLabel(
    coachStatusData?.coachStatus?.states ?? [],
  );
  if (
    coachStatusLoading ||
    coachStatusFetching ||
    coachStatusData === undefined
  )
    coachStatusText = 'Loading...';
  if (coachStatusError) coachStatusText = 'Unable to load status.';

  const onEditingCoach = () => setIsEditing(true);
  const onEditingCoachClose = () => setIsEditing(false);
  const onEditingAdminDetails = () => {
    if (showAdminOnly) setIsEditingAdminDetails(true);
  };
  const onEditingAdminDetailsClose = () => setIsEditingAdminDetails(false);
  const canViewSubFunctions =
    showAdminOnly || (isCoach(currentUser) && currentUser.id === id);
  // The Sessions/Members tabs embed the Mento Calendar schedule, so they exist
  // only for a coach whose scheduling provider is Moments. While the next
  // route's coach loads, `coach` is still the previous one; the render guard
  // below shows nothing until the ids match, and keeping its tabs meanwhile
  // preserves the selected tab across the transition.
  const calendarEnabled = showAdminOnly && usesMomentsScheduling(coach);
  const tabs = useMemo(
    () => [
      { key: 'profile', label: 'Profile' },
      ...(showAdminOnly
        ? [
            { key: 'summary', label: 'Summary' },
            ...(calendarEnabled
              ? [
                  { key: 'sessions', label: 'Sessions' },
                  { key: 'members', label: 'Members' },
                ]
              : []),
            { key: 'notes', label: 'Notes' },
            { key: 'functions', label: 'Functions' },
          ]
        : []),
      ...(canViewSubFunctions
        ? [{ key: 'subFunctions', label: 'Sub-functions' }]
        : []),
    ],
    [calendarEnabled, canViewSubFunctions, showAdminOnly],
  );

  const defaultTabKey = tabs[initialTabIndex]?.key ?? 'profile';
  const [activeTab, setActiveTab] = useState(defaultTabKey);
  const activeTabIndex = tabs.findIndex((tab) => tab.key === activeTab);
  const safeTabIndex = activeTabIndex >= 0 ? activeTabIndex : 0;

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    let requestedTab = null;
    if (tabParam === 'sub-functions') requestedTab = 'subFunctions';
    if (tabParam === 'functions') requestedTab = 'functions';
    if (tabParam === 'sessions') requestedTab = 'sessions';
    if (tabParam === 'members') requestedTab = 'members';
    if (!requestedTab) return;

    if (tabs.some((tab) => tab.key === requestedTab))
      setActiveTab(requestedTab);
  }, [searchParams, tabs]);

  useEffect(() => {
    if (activeTabIndex < 0) setActiveTab(defaultTabKey);
  }, [activeTabIndex, defaultTabKey]);

  if (isLoading || isUserLoading || !coach || coach.id !== id) {
    return null;
  }

  return (
    <div className="flex-1 flex-col bg-sunny-100 md:flex">
      <div className="w-full bg-sage-500 pl-2 pt-4 lg:pl-8 lg:pt-0">
        <Banner user={coach} onEdit={onEditingCoach} />
      </div>
      <div className="block px-6 py-4 lg:container md:px-8 lg:mt-8 lg:flex lg:flex-1 lg:px-12">
        <div className="w-full lg:flex">
          {/* Left column: Admin details stays constant across tabs */}
          <div className="-mx-8 -mb-4 mt-12 bg-sage-200 px-8 py-4 lg:mx-0 lg:mt-6 lg:size-64 lg:bg-transparent lg:px-0 lg:pb-6">
            <Details
              coach={coach}
              onEditAdmin={showAdminOnly ? onEditingAdminDetails : undefined}
            />
            {canViewCoachStatus && (
              <Typography className="mt-4 text-sage-600" variant="subtitle2">
                Coach status: {coachStatusText}
              </Typography>
            )}
          </div>

          {/* Right column: Tabs and tab content (Bio/Summary/etc.) */}
          <div className="space-y-8 px-0 lg:flex-1 lg:px-10">
            <Tabs
              value={safeTabIndex}
              onChange={(_, value) => {
                const selectedTab = tabs[value];
                if (selectedTab) setActiveTab(selectedTab.key);
              }}
            >
              {tabs.map((t, idx) => (
                <Tab key={t.key} label={t.label} value={idx} />
              ))}
            </Tabs>

            {activeTab === 'profile' && (
              <>
                <Bio user={coach} />
                {isAdmin(currentUser) && coach.coachProfile?.bragSheet && (
                  <section className="max-w-2xl space-y-4">
                    <Typography variant="h5">
                      Brag Sheet (Admin only)
                    </Typography>
                    <div data-color-mode="light">
                      <MDEditor.Markdown
                        source={coach.coachProfile.bragSheet}
                      />
                    </div>
                  </section>
                )}
                <Experience
                  coach={coach}
                  industries={coach.coachProfile?.industries}
                />
                <Styles styles={coach.coachProfile?.styles ?? []} />
              </>
            )}

            {canViewSubFunctions && activeTab === 'subFunctions' && (
              <EditCoachSubFunctions
                canEdit={showAdminOnly}
                coach={coach}
                refetchCoach={refetch}
              />
            )}
            {showAdminOnly && activeTab === 'functions' && (
              <EditCoachFunctions coach={coach} refetchCoach={refetch} />
            )}
            {showAdminOnly && activeTab === 'summary' && (
              <CoachSummary coach={coach} />
            )}
            {showAdminOnly && activeTab === 'sessions' && (
              <CoachSchedulePage embeddedCoach={coach} />
            )}
            {showAdminOnly && activeTab === 'members' && (
              <section aria-labelledby="coach-members-heading">
                <Typography id="coach-members-heading" variant="h5">
                  Members
                </Typography>
                <ul className="mt-4 space-y-2">
                  {coach.coachees.map((member) => (
                    <li key={member.id}>
                      <Typography variant="body1">{member.fullName}</Typography>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {showAdminOnly &&
              activeTab === 'notes' &&
              (coach.coachProfile?.id ? (
                <Notes
                  owneableId={coach.coachProfile.id}
                  owneableType={NoteOwnerType.COACH_PROFILE}
                  title="Admin Notes"
                />
              ) : (
                <Typography variant="body2">
                  Coach profile not available.
                </Typography>
              ))}
          </div>
        </div>
      </div>

      {showAdminOnly && !!isEditingAdminDetails && (
        <EditCoachAdmin
          key={coach.id}
          canManageCoachStatus={canManageCoachStatus}
          coach={coach}
          refetchCoach={refetch}
          onClose={onEditingAdminDetailsClose}
        />
      )}
      {!!isEditing && (
        <EditCoach
          coach={coach}
          refetchCoach={refetch}
          onClose={onEditingCoachClose}
        />
      )}
    </div>
  );
};

export default Profile;
